import type { ServerMessage } from '@playplate/shared';
import { api } from './api';

/**
 * Realtime client.
 *
 * Three properties that matter more than the socket itself:
 *
 *  1. It authenticates with a single-use ticket fetched over the authenticated
 *     REST API, because a WebSocket handshake is not covered by CORS.
 *  2. It reconnects with exponential backoff and jitter, forever. A wall-mounted
 *     tablet on store Wi-Fi will drop; the operator should never have to know.
 *  3. On every reconnect it reports the gap so the app can replay missed events
 *     over REST. The socket is an accelerator, not the source of truth — an
 *     alert raised while the connection was down is still in Postgres, and the
 *     UI fetches it rather than pretending it never happened.
 */

export type RealtimeStatus = 'connecting' | 'open' | 'reconnecting' | 'offline';

type Listener = (message: ServerMessage) => void;
type StatusListener = (status: RealtimeStatus, detail: { since: string | null }) => void;

const MAX_BACKOFF_MS = 30_000;
const PING_INTERVAL_MS = 25_000;

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private status: RealtimeStatus = 'offline';
  private attempt = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private stopped = false;
  /** Timestamp of the last message received, used to bound the replay window. */
  private lastMessageAt: string | null = null;

  connect(): void {
    this.stopped = false;
    void this.open();
  }

  disconnect(): void {
    this.stopped = true;
    this.clearTimers();
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
    this.setStatus('offline');
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status, { since: this.lastMessageAt });
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener(status, { since: this.lastMessageAt });
  }

  private async open(): Promise<void> {
    if (this.stopped) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let ticket: string;
    try {
      const response = await api.post<{ ticket: string }>('/auth/ws-ticket');
      ticket = response.ticket;
    } catch {
      // No ticket means either the session is gone (the API client has already
      // dispatched the expiry event) or the API is down. Either way: back off.
      this.scheduleReconnect();
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${window.location.host}/ws?ticket=${encodeURIComponent(ticket)}`);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setStatus('open');
      this.startPing();
    };

    socket.onmessage = (event) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data as string) as ServerMessage;
      } catch {
        return;
      }
      if ('ts' in message && typeof message.ts === 'string') this.lastMessageAt = message.ts;
      for (const listener of this.listeners) listener(message);
    };

    socket.onerror = () => {
      // onclose always follows; handling it in one place avoids double backoff.
    };

    socket.onclose = (event) => {
      this.clearTimers();
      this.socket = null;
      if (this.stopped) return;
      // 4401/4403 mean the session is not valid — reconnecting in a loop would
      // hammer the API for a ticket it will never issue.
      if (event.code === 4401 || event.code === 4403) {
        this.setStatus('offline');
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.attempt += 1;
    this.setStatus('reconnecting');
    // Full jitter: twelve tablets in one store must not all retry in lockstep
    // the moment the API comes back.
    const ceiling = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(this.attempt, 5));
    const delay = Math.round(Math.random() * ceiling);
    this.reconnectTimer = window.setTimeout(() => void this.open(), delay);
  }

  private startPing(): void {
    this.pingTimer = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL_MS);
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
  }

  /** Where a replay should start from after a gap in the connection. */
  get replayFrom(): string | null {
    return this.lastMessageAt;
  }
}

export const realtime = new RealtimeClient();
